import React from 'react';

interface UseClampedPositionProps {
    position: { x?: number; y?: number };
    size: { width: number; height: number };
    containerRect: DOMRect | undefined;
}

interface UseClampedPositionReturn {
    clampedStyle: React.CSSProperties;
    clampedX: number;
    clampedY: number;
    relativePointerX: number;
    relativePointerY: number;
}

export const useClampedPosition = ({
    position,
    size,
    containerRect,
}: UseClampedPositionProps): UseClampedPositionReturn => {
    return React.useMemo(() => {
        const clampedStyle: React.CSSProperties = {
            position: 'absolute',
            pointerEvents: 'none',
        };

        const result: UseClampedPositionReturn = {
            clampedStyle,
            clampedX: 0,
            clampedY: 0,
            relativePointerX: 0,
            relativePointerY: 0,
        };

        if (!containerRect) return result;

        const transforms: string[] = [];

        const clamp = (
            val: number,
            containerDim: number,
            elementDim: number,
        ) => {
            const halfSize = elementDim / 2;
            return Math.max(halfSize, Math.min(val, containerDim - halfSize));
        };

        // Handle Horizontal Clamping
        if (position.x !== undefined && containerRect.width) {
            result.clampedX = clamp(
                position.x,
                containerRect.width,
                size.width,
            );
            result.relativePointerX = position.x - result.clampedX;
            clampedStyle.left = `${result.clampedX}px`;
            transforms.push('translateX(-50%)');
        }

        // Handle Vertical Clamping
        if (position.y !== undefined && containerRect.height) {
            result.clampedY = clamp(
                position.y,
                containerRect.height,
                size.height,
            );
            result.relativePointerY = position.y - result.clampedY;
            clampedStyle.top = `${result.clampedY}px`;
            transforms.push('translateY(-50%)');
        }

        if (transforms.length > 0) {
            clampedStyle.transform = transforms.join(' ');
        }

        return result;
    }, [containerRect, position, size]);
};
